/**
 * Natural cubic spline interpolation.
 * Ported from the Flutter widget's cubicSplineInterpolate function.
 */
export class CubicSpline {
  private xs: number[];
  private ys: number[];
  private M: number[];

  constructor(xs: number[], ys: number[]) {
    if (xs.length !== ys.length || xs.length < 2) {
      this.xs = [0, 1];
      this.ys = [0, 0];
      this.M = [0, 0];
      return;
    }

    this.xs = xs;
    this.ys = ys;
    this.M = this.computeSecondDerivatives();
  }

  private computeSecondDerivatives(): number[] {
    const n = this.xs.length;
    const h: number[] = new Array(n - 1);
    for (let i = 0; i < n - 1; i++) {
      h[i] = this.xs[i + 1] - this.xs[i];
    }

    const alpha: number[] = new Array(n).fill(0);
    for (let i = 1; i < n - 1; i++) {
      alpha[i] =
        (3 / h[i]) * (this.ys[i + 1] - this.ys[i]) -
        (3 / h[i - 1]) * (this.ys[i] - this.ys[i - 1]);
    }

    // Thomas algorithm for tridiagonal system (natural boundary: M[0] = M[n-1] = 0)
    const l: number[] = new Array(n).fill(0);
    const mu: number[] = new Array(n).fill(0);
    const z: number[] = new Array(n).fill(0);
    l[0] = 1;

    for (let i = 1; i < n - 1; i++) {
      l[i] = 2 * (this.xs[i + 1] - this.xs[i - 1]) - h[i - 1] * mu[i - 1];
      mu[i] = h[i] / l[i];
      z[i] = (alpha[i] - h[i - 1] * z[i - 1]) / l[i];
    }

    l[n - 1] = 1;
    z[n - 1] = 0;

    const M: number[] = new Array(n).fill(0);
    for (let j = n - 2; j >= 0; j--) {
      M[j] = z[j] - mu[j] * M[j + 1];
    }

    return M;
  }

  evaluate(x: number): number {
    const n = this.xs.length;

    if (x <= this.xs[0]) return this.ys[0];
    if (x >= this.xs[n - 1]) return this.ys[n - 1];

    // Binary search for interval
    let lo = 0;
    let hi = n - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (this.xs[mid] <= x) lo = mid;
      else hi = mid;
    }

    const i = lo;
    const h_i = this.xs[i + 1] - this.xs[i];
    const a = (this.xs[i + 1] - x) / h_i;
    const b = (x - this.xs[i]) / h_i;

    return (
      a * this.ys[i] +
      b * this.ys[i + 1] +
      ((a * a * a - a) * this.M[i] + (b * b * b - b) * this.M[i + 1]) *
        (h_i * h_i) /
        6.0
    );
  }
}
